import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Lock } from "lucide-react";
import { adminLogin, setToken, getToken, adminMe, clearToken } from "@/lib/api";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [needs2fa, setNeeds2fa] = useState(false);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("expired")) {
      toast.message("Your session ended. Please sign in again.");
    }
    if (getToken()) {
      adminMe()
        .then(() => navigate("/admin"))
        .catch(() => clearToken());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await adminLogin({
        email,
        password,
        totp_code: needs2fa ? code : undefined,
      });
      setToken(res.access_token);
      toast.success("Signed in.");
      navigate("/admin");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail === "2FA_REQUIRED") {
        setNeeds2fa(true);
        toast.message("Enter your 6-digit authenticator code.");
      } else {
        toast.error(detail || "Sign in failed.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-sand px-6">
      <div className="w-full max-w-sm bg-white border border-border rounded-2xl p-8 shadow-[0_8px_32px_rgba(17,42,34,0.05)]">
        <div className="grid h-11 w-11 place-items-center rounded-xl bg-ink text-white">
          <Lock className="h-5 w-5" strokeWidth={2} />
        </div>
        <h1 className="mt-5 font-heading text-2xl text-ink">Admin sign in</h1>
        <p className="mt-1 text-sm text-ink-muted">Restricted area.</p>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <Label htmlFor="email" className="text-ink font-medium">Email</Label>
            <Input
              id="email"
              type="email"
              data-testid="admin-email-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="mt-2 bg-white border-border focus-visible:ring-clay rounded-xl py-6"
            />
          </div>
          <div>
            <Label htmlFor="password" className="text-ink font-medium">Password</Label>
            <Input
              id="password"
              type="password"
              data-testid="admin-password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="mt-2 bg-white border-border focus-visible:ring-clay rounded-xl py-6"
            />
          </div>
          {needs2fa && (
            <div>
              <Label htmlFor="code" className="text-ink font-medium">2FA code</Label>
              <Input
                id="code"
                inputMode="numeric"
                data-testid="admin-2fa-input"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="123456"
                className="mt-2 bg-white border-border focus-visible:ring-clay rounded-xl py-6 tracking-widest"
              />
            </div>
          )}
          <Button
            type="submit"
            data-testid="admin-login-button"
            disabled={loading}
            className="w-full bg-clay hover:bg-clay-hover text-white rounded-lg py-6 font-medium"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
          </Button>
        </form>
      </div>
    </div>
  );
}
